'use client'

import ChevronLeftIcon from '@heroicons/react/20/solid/ChevronLeftIcon'
import ChevronRightIcon from '@heroicons/react/20/solid/ChevronRightIcon'
import type { KeenSliderInstance } from 'keen-slider/react'
import { useKeenSlider } from 'keen-slider/react'
import { usePathname } from 'next/navigation'
import { createRef, useEffect, useMemo, useState } from 'react'

import { UpcomingOrdersIndicator } from '@src/app/[country]/[lang]/account/_modules/UpcomingOrdersIndicator'
import { Container } from '@src/components/Container/Container'
import { LocalizedLink } from '@src/components/LocalizedLink/LocalizedLink'
import { Recaptcha } from '@src/components/Recaptcha/Recaptcha'
import { configPublic } from '@src/config.public'
import { useLocale } from '@src/i18n/LocaleProvider'
import { getUnlocalizedPath } from '@src/i18n/locale.util'
import { useBreakpoints } from '@src/utils/breakpoints.util'
import { cn } from '@src/utils/tailwind'

export function AccountHeader() {
  const { t, locale } = useLocale('accountPage')
  const { isMobile } = useBreakpoints()
  const pathname = usePathname()
  const navLinks = useMemo(() => {
    const navLinks: { href: string; text: string }[] = []

    navLinks.push({
      href: '/account/settings',
      text: 'accountSettings',
    })

    if (configPublic.featureFlags.savedAddressesEnabled && locale.savedAddressesEnabled) {
      navLinks.push({
        href: '/account/addresses',
        text: 'shippingAddresses',
      })
    }

    if (configPublic.featureFlags.savedPaymentsEnabled && locale.savedPaymentsEnabled) {
      navLinks.push({
        href: '/account/payments',
        text: 'paymentMethods',
      })
    }

    navLinks.push({
      href: '/account/orders',
      text: 'orders',
    })

    navLinks.push({
      href: '/account/subscriptions',
      text: 'subscriptions',
    })

    return navLinks
  }, [locale.savedAddressesEnabled, locale.savedPaymentsEnabled])

  const navLinkRefs = useMemo(() => navLinks.map(() => createRef<HTMLAnchorElement>()), [navLinks])
  const activeNavLink = useMemo(
    () => navLinks.find((link) => getUnlocalizedPath(pathname) === link.href),
    [navLinks, pathname]
  )

  const [sliderReady, setSliderReady] = useState(false)
  const [isSliderScrollable, setIsSliderScrollable] = useState({ prev: false, next: false })
  const [ref, slider] = useKeenSlider<HTMLDivElement>({
    mode: 'free',
    rubberband: false,
    slides: { perView: 'auto', spacing: 32 },
    created: syncSlider,
    updated: syncSlider,
    detailsChanged: syncSlider,
  })

  function syncSlider(slider: KeenSliderInstance) {
    const { position, min, max, length } = slider.track.details
    const isOverflowing = length > 0
    setIsSliderScrollable({
      prev: isOverflowing && position > min,
      next: isOverflowing && position < max,
    })
    setSliderReady(true)
  }

  useEffect(
    function scrollIntoView() {
      if (!activeNavLink) return
      const index = navLinks.indexOf(activeNavLink)
      slider.current?.slides[index].scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    },
    [slider, activeNavLink, isMobile, navLinks]
  )

  return (
    <Container className="pt-12 lg:pt-20">
      <h1 className="text-heading-display-1 mb-1">{t('account')}</h1>
      <p className="mb-4 text-body-lg">{t('accountSubheader')}</p>
      <div className="relative -left-4 w-screen sm:-left-8 lg:left-0 lg:w-full">
        <nav
          ref={ref}
          data-testid="account-nav"
          className={cn('keen-slider', {
            'gap-8': !sliderReady,
            'lg:[mask-image:linear-gradient(90deg,rgba(255,255,255,0)_2%,rgba(0,0,0,1)_5%,rgba(0,0,0,1)_50%,rgba(0,0,0,1)_95%,rgba(255,255,255,0)_98%)]':
              isSliderScrollable.prev && isSliderScrollable.next,
            'lg:[mask-image:linear-gradient(90deg,rgba(255,255,255,0)_2%,rgba(0,0,0,1)_5%,rgba(0,0,0,1)_50%)]':
              isSliderScrollable.prev && !isSliderScrollable.next,
            'lg:[mask-image:linear-gradient(90deg,rgba(0,0,0,1)_95%,rgba(255,255,255,0)_98%)]':
              !isSliderScrollable.prev && isSliderScrollable.next,
          })}
        >
          {navLinks.map((link, index) => {
            const isActive = link === activeNavLink
            return (
              <div
                key={link.href}
                className={cn(
                  'keen-slider__slide min-w-fit max-w-fit',
                  'first-of-type:pl-4 last-of-type:pr-4 sm:first-of-type:pl-8 sm:last-of-type:pr-8',
                  'lg:first-of-type:pl-0 lg:last-of-type:pr-0'
                )}
              >
                <div className="flex items-center gap-[2px]">
                  <LocalizedLink
                    data-testid={`account-nav-link-${index}`}
                    className={cn(
                      'flex min-w-fit max-w-fit gap-px whitespace-nowrap border-b-[3px] pb-3 pt-4 hover:text-bodi-black-text',
                      {
                        'text-bodi-gray-medium border-transparent': !isActive,
                        'text-bodi-black-text font-medium border-bodi-black-text': isActive,
                      }
                    )}
                    href={link.href}
                    ref={navLinkRefs[navLinks.indexOf(link)]}
                  >
                    {t(link.text)}
                  </LocalizedLink>
                  {link.text === 'subscriptions' && <UpcomingOrdersIndicator />}
                </div>
              </div>
            )
          })}
        </nav>
        <button
          onClick={() => {
            if (!slider.current) return
            const { moveToIdx, track } = slider.current
            const currentIndex = track.details.slides.findIndex((slide) => slide.portion >= 1) ?? 0
            const prevIndex = Math.max(track.details.minIdx, currentIndex - 4)
            moveToIdx(prevIndex)
          }}
          className={cn('absolute -left-2 top-0 hidden h-full', {
            'lg:block': isSliderScrollable.prev,
          })}
        >
          <ChevronLeftIcon className="size-7" />
        </button>
        <button
          onClick={() => {
            if (!slider.current) return
            const { moveToIdx, track } = slider.current
            const currentIndex = track.details.slides.findIndex((slide) => slide.portion >= 1) ?? 0
            const nextIdx = Math.min(track.details.maxIdx, currentIndex + 3)
            moveToIdx(nextIdx)
          }}
          className={cn('absolute -right-2 top-0 hidden h-full', {
            'lg:block': isSliderScrollable.next,
          })}
        >
          <ChevronRightIcon className="size-7" />
        </button>
      </div>
      <Recaptcha />
    </Container>
  )
}
